import createCachedSelector from 're-reselect';
import { useSelector } from 'react-redux';
import { ModelsState, RootState } from 'redux/schemas';
import { NormalizedTimeline } from 'redux/schemas/models/timeline';
import _, { each } from 'underscore';
import { ActivityType } from 'redux/schemas/models/activity';
import { NLecturePage } from 'redux/schemas/models/lecture-page';
import { lectureStatusSelector } from 'redux/selectors/lecture-page';
import { CourseOutlineItem } from './course-outline-item';
import { getActivityIdsFromLecturePageId } from '../timeline';

type OutlineLecturePages = { [id: string]: NLecturePage & { achievableTotalPoints: number } };

/** Creates a flat description of a course's sections, subsections, and lesson pages the correct
 order per how they they should appear in the LHS timeline navigation. Also used by the move-to
 component submenu and other UIs that show an "outline" of the course. */
const computeOutline = (
  lectureSections: ModelsState['lectureSections'],
  // Note that this param currently causes this function to re-fire every time we change a lecture page.
  // This is because we load more lecture page data, thus changing this part of our model state
  lecturePages: OutlineLecturePages,
  timeline: NormalizedTimeline,
) => {
  const outline: CourseOutlineItem[] = [];

  if (!timeline) {
    return [];
  }

  const createSectionLecturePageItems = (sectionId: number, sectionType: 'section' | 'subsection') => {
    const items = [];
    const section = lectureSections[sectionId];
    let sectionPointsAccrued = 0;
    let sectionTotalPoints = 0;
    let sectionAchievableTotalPoints = 0;
    let lecturesAreReleased = false;

    section.lecturePages.forEach(lectureId => {
      const lecturePage = lecturePages[lectureId];

      // TODO: Is there a separate prop for manually awarded points?
      const lecturePointsAccrued = lecturePage.pointsReceived;
      // Total points is an array of length 2 due to an old feature which caused points to "decay", where
      // point values would decrease over time. The 0th index point value is the only one we should use
      // going forward
      const [lectureTotalPoints] = lecturePage.totalPoints ?? [0];

      items.push({
        type: 'lecture',
        name: lecturePage.title,
        id: lectureId,
        section: sectionId,
        sectionType,
        pointsAccrued: lecturePointsAccrued,
        totalPoints: lectureTotalPoints,
        completed: lecturePage.completed,
        achievableTotalPoints: lecturePage.achievableTotalPoints,
      });

      sectionTotalPoints += lectureTotalPoints;
      sectionPointsAccrued += lecturePointsAccrued;
      sectionAchievableTotalPoints += lecturePage.achievableTotalPoints;
      lecturesAreReleased = lecturesAreReleased || lecturePage.released;
    });

    return {
      items,
      totalPoints: sectionTotalPoints,
      pointsAccrued: sectionPointsAccrued,
      currentTotalPoints: sectionAchievableTotalPoints,
      lecturesAreReleased,
    };
  };

  const addSectionToOutline = (sectionId: number, type: 'section' | 'subsection') => {
    const lectureSection = lectureSections[sectionId];

    const lecturePageItems = createSectionLecturePageItems(sectionId, type);
    const sectionOutlineItem = {
      type,
      name: lectureSection.title,
      id: sectionId,
      pointsAccrued: type === 'subsection' ? lecturePageItems.pointsAccrued : undefined,
      totalPoints: type === 'subsection' ? lecturePageItems.totalPoints : undefined,
      achievableTotalPoints: type === 'subsection' ? lecturePageItems.currentTotalPoints : undefined,
      allLecturePagesCompleted: lecturePageItems.items.length > 0 ? _.every(lecturePageItems.items, (item) => item.completed) : false,
      lecturesAreReleased: lecturePageItems.lecturesAreReleased,
    };

    outline.push(sectionOutlineItem);
    outline.push(...lecturePageItems.items);

    return sectionOutlineItem;
  };

  timeline.lectureSubsections.forEach(lssId => addSectionToOutline(lssId, 'subsection'));

  timeline.lectureSections.forEach(lId => {
    const sectionOutlineItem = addSectionToOutline(lId, 'section');
    lectureSections[lId].lectureSubsections.forEach(lssId => {
      const subsectionOutlineItem = addSectionToOutline(lssId, 'subsection');
      // This tracks sections who have any descendants who are lectures that are released. Learners should only see & interact with sections/subsections/lessons
      // that are released or who have released descendants.
      sectionOutlineItem.lecturesAreReleased = sectionOutlineItem.lecturesAreReleased || subsectionOutlineItem.lecturesAreReleased;
    });
  });

  return outline;
};

const getLectureAchievedPoints = (state: RootState, lecturePageId: number) => {
  const activities = getActivityIdsFromLecturePageId(state, lecturePageId);

  return _.reduce(activities, (total, activityIds: number[], activityType: ActivityType) => (
    total + _.reduce(activityIds, (memo, activityId) => {
      const activity = state.models[activityType][activityId];
      return memo + (activity?.pointsReceived ?? 0);
    }, 0)
  ), 0);
};

const getLectureAchievableTotalPoints = (state: RootState, lecturePageId: number) => {
  const activities = getActivityIdsFromLecturePageId(state, lecturePageId);

  return _.reduce(activities, (total, activityIds: number[], activityType: ActivityType) => (
    total + _.reduce(activityIds, (memo, activityId) => {
      const activity = state.models[activityType][activityId];
      if (activity?.progress === 'completed') {
        return memo + activity.pointsReceived;
      }
      return memo + ((!activity?.totalPoints || activity?.expired || activity?.progress === 'missed') ? 0 : activity.totalPoints[0]);
    }, 0)
  ), 0);
};

const getLecturePageswithAchievablePoints = (state: RootState) => {
  const { lecturePages } = state.models;
  const LPWithAchievablePoints: OutlineLecturePages = {};
  each(lecturePages, (lp) => {
    LPWithAchievablePoints[lp.id] = {
      ...lp,
      pointsReceived: getLectureAchievedPoints(state, lp.id),
      achievableTotalPoints: !_.isEmpty(lp.totalPoints) ? getLectureAchievableTotalPoints(state, lp.id) : 0,
      completed: lectureStatusSelector(state, lp.id) === 'completed',
    };
  });

  return LPWithAchievablePoints;
};

const getTimeline = (state: RootState, catalogId: string) => state.models.timelines[catalogId];
const getLectureSections = (state: RootState) => state.models.lectureSections;


/** Creates a selector to compute & store the course outline created via computeOutline(). This is achieved
 * via the re-reselect library as one of the input parameters (catalogId) is a non-Redux state value, which
 * necessitates reselect using multiple caches (see https://github.com/toomuchdesign/re-reselect#re-reselect for
 * more info) */
const courseOutlineSelector = createCachedSelector(
  getLecturePageswithAchievablePoints,
  getTimeline,
  getLectureSections,
  (lecturePages, timeline, lectureSections) => computeOutline(lectureSections, lecturePages, timeline),
)(
  (state, catalogId) => catalogId,
);

export type CourseOutline = CourseOutlineItem[];

/** Calculates and returns an object describing the "outline" (sometimes referred to as a "timeline")for this course.
 * This is a computed version of the data returned by /course_timeline.json and is the primary way
 * that we describe the ordering of sections, subsections, & lectures in our application. */
export const useCourseOutline = (catalogId: string): CourseOutline => useSelector(state => courseOutlineSelector(state, catalogId));

// TODO: Remove all of the blow

// TODO: Used for performance testing cached selectors in the timeline; remove when finished testing

// const testComputeOutline = (lectureSection: NLectureSection) => {
//   console.log('doing something in testComputeOultine', lectureSection);
//   return `a section: ${lectureSection.id}`;
// };

// const testCourseOutlineSelector = createCachedSelector(
//   (lectureSections: ModelsState['lectureSections'], catalogId: string) => {
//     console.log('catalogId in testCourseOutlineSelector:', catalogId);
//     return lectureSections[Object.keys(lectureSections)[0]];
//   },
//   (lectureSection) => testComputeOutline(lectureSection),
// )(
//   (state, catalogId) => catalogId,
// );

// export const useTestOutline = (catalogId: string): any => useSelector(state => testCourseOutlineSelector(state.models.lectureSections, catalogId));

// TODO: The below was written when first attempting to include points data. Unused, likely not useful. Delete

const lecturePageSelector = (state: RootState, lecturePageId: number) => state.models.lecturePages[lecturePageId];
const lectureComponentsSelector = (state: RootState, lecturePageId: number) => state.models.lecturePages[lecturePageId].lectureComponents.map(lcId => state.models.lectureComponents[lcId]);

// const lecturePageCompletionSelector = createCachedSelector(
//   state => state,
//   lecturePageSelector,
//   lectureComponentsSelector,
//   (state, lecturePage, lectureComponents) => computeLecturePageCompletion(state, lecturePage, lectureComponents),
// )(
//   (state, lecturePageId) => lecturePageId,
// );


// const computeLecturePageCompletion = (state: RootState, lecturePage: NLecturePage, lectureComponents: NLectureComponent[]): LecturePageProgress => {

// TODO: How does any of this logic being from isFullyCompleted() relate to lectureComponent.completed?


// if (!lecturePage.viewed) {
//   return 'not-started';
// }

// if (!lectureComponents || lectureComponents.length === 0) {
//   return (lecturePage.completed && lecturePage.viewed) ? 'completed'
// }

// const allComponentsCompleted = _.every(lectureComponents, component => {
//   // TODO: Fill out the getIsTodo func
//   if (getIsTodo(state, component.id)) {
//     return getStatus(state, component.id) === 'completed';
//     // TODO: Add a getIsApproved()
//   }
//   return false;
// });

// if (allComponentsCompleted) {
//   return lecturePage.completed
// }

// const componentsWithPoints = _.filter(lectureComponents, lc => lc.is)

// lectureComponents.forEach;

// return {
//   pointsAccrued: 0,
//   totalPoints: 0,
// };
// };
